import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, MapPin, ShieldCheck, Sparkles, ArrowLeft, Share2, Copy, Check, Users, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ApexLoader from "@/components/ApexLoader";
import api from "@/lib/api";

export default function FoundingMemberDetail() {
  const { memberId } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/founding-members/public/${memberId}`);
        setMember(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [memberId]);

  useEffect(() => {
    if (member) {
      document.title = `${member.company} · Founding Member · Apex Infinity`;
    }
    return () => {
      document.title = "Apex Infinity Ventures";
    };
  }, [member]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const share = async () => {
    const text = `${member.company} is a founding member of Apex Infinity — restoration, at the speed of AI.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: member.company, text, url: shareUrl });
        return;
      } catch (err) {
        console.debug("Web Share unavailable or cancelled, falling back to copy", err);
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      console.error("Clipboard write failed", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col">
      <Navbar />

      <main className="flex-1 pt-28 md:pt-32 pb-24" data-testid="founding-member-detail">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Link
            to="/#founding-members"
            className="inline-flex items-center gap-2 font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white mb-10"
            data-testid="back-to-wall"
          >
            <ArrowLeft size={12} /> Back to the wall
          </Link>

          {loading && (
            <div className="flex flex-col items-center gap-5 py-16" data-testid="loading-seat">
              <ApexLoader inline size={48} />
              <div className="font-mono-apex text-[10px] uppercase tracking-[0.35em] text-zinc-500">
                loading seat
              </div>
            </div>
          )}

          {!loading && error === "not_found" && (
            <div className="border border-white/10 bg-[#121214] p-12 text-center" data-testid="member-not-found">
              <Sparkles size={28} className="text-[#dc2626] mx-auto mb-5" strokeWidth={1.5} />
              <div className="font-display text-4xl text-white mb-4">This seat isn't claimed.</div>
              <p className="text-zinc-400 mb-8">
                Either the link is wrong or the application isn't approved yet.
              </p>
              <Link
                to="/#founding-members"
                className="inline-flex items-center gap-2 btn-primary-apex px-7 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em]"
              >
                See the wall <ArrowLeft size={14} className="rotate-180" />
              </Link>
            </div>
          )}

          {!loading && member && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="border border-white/10 bg-[#121214]"
            >
              <div className="relative p-10 md:p-14 border-b border-white/5 overflow-hidden">
                <div className="absolute inset-0 opacity-30 pointer-events-none"
                     style={{ background: "radial-gradient(600px circle at 90% 0%, rgba(220,38,38,0.25), transparent 60%)" }} />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="block w-8 h-px bg-[#dc2626]" />
                    <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                      founding member · seat #{String(member.seat_number || 0).padStart(3, "0")}
                    </span>
                  </div>
                  <Award size={36} className="text-[#dc2626] mb-6" strokeWidth={1.5} />
                  <h1 className="font-display text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-6" data-testid="member-company">
                    {member.company}
                  </h1>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-apex text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={12} />
                      {member.city ? `${member.city}, ${member.state}` : member.state} · {member.primary_zip}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Users size={12} /> {member.crews_count} crew{member.crews_count === 1 ? "" : "s"}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} /> {member.years_active} years active
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-10 md:p-14 grid md:grid-cols-2 gap-10">
                <div>
                  <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                    services
                  </div>
                  {member.services?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {member.services.map((s) => (
                        <span key={s} className="px-3 py-1.5 border border-white/10 bg-[#0a0a0b] font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-200">
                          {s}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-zinc-500 text-sm">Full-service restoration</div>
                  )}
                </div>

                <div>
                  <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                    certifications
                  </div>
                  {member.certifications ? (
                    <div className="flex items-start gap-2 text-zinc-200">
                      <ShieldCheck size={16} className="text-[#dc2626] mt-0.5 shrink-0" />
                      <span>{member.certifications}</span>
                    </div>
                  ) : (
                    <div className="text-zinc-500 text-sm">Vetted through Apex founding review</div>
                  )}
                </div>
              </div>

              <div className="p-10 md:p-14 border-t border-white/5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
                <div>
                  <div className="font-display text-2xl text-white mb-1">Share this seat</div>
                  <p className="text-zinc-400 text-sm">
                    {member.company} is one of the first 100 restoration companies on Apex. Post it.
                  </p>
                </div>
                <button
                  onClick={share}
                  className="inline-flex items-center gap-2 btn-primary-apex px-7 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em]"
                  data-testid="share-seat-btn"
                >
                  {copied ? (
                    <>
                      <Check size={14} /> Link copied
                    </>
                  ) : (
                    <>
                      <Share2 size={14} /> Share seat
                    </>
                  )}
                </button>
              </div>

              <div className="px-10 md:px-14 pb-10 md:pb-14">
                <div className="flex items-center gap-2 px-4 py-3 bg-[#0a0a0b] border border-white/10 font-mono-apex text-[10px] uppercase tracking-[0.15em] text-zinc-400 overflow-hidden">
                  <Copy size={12} className="shrink-0 text-zinc-500" />
                  <span className="truncate" data-testid="permalink-url">{shareUrl}</span>
                </div>
              </div>
            </motion.div>
          )}

          <div className="mt-16 text-center">
            <Link
              to="/#join-network"
              className="inline-flex items-center gap-2 btn-secondary-apex px-7 py-4 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
              data-testid="detail-apply-cta"
            >
              Apply for a founding seat
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
